import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { courses, logoUrl, contactInfo } from '../data/mock';
import { 
  CheckCircle2, 
  Users, 
  Dumbbell, 
  BookOpen,
  ArrowRight,
  Star,
  Mail
} from 'lucide-react';

const CoursesPage = () => {
  const handleBookConsultation = () => {
    const subject = encodeURIComponent('Course Inquiry - Jay Defence Academy');
    const body = encodeURIComponent('Hello,\\n\\nI would like to know more about your courses.\\n\\nPlease contact me at your earliest convenience.\\n\\nThank you.');
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  const features = [
    { icon: Users, title: 'Expert Trainers', desc: 'Special trainers who are either veterans or specialists in their fields!' },
    { icon: Dumbbell, title: 'Physical Fitness', desc: 'All courses include a physical training module to ensure you are in the best shape for SSB.' },
    { icon: BookOpen, title: 'Daily Classes', desc: 'Daily classes for select programs include all required modules for clearing exams and improving personality.' }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800/90 to-olive-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={logoUrl} alt="Jay Defence Academy" className="h-16 mx-auto mb-6" />
          <span className="inline-block text-gold-400 font-semibold text-sm tracking-wider uppercase mb-4">Our Programs</span>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Learn From Armed Forces <span className="text-gold-400">Veterans</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Serve the Nation. Join Forces With JDEF. Our comprehensive programs are designed 
            to prepare you for success in your defence career.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-cream-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-gold-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-1">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Choose Your Program</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Select the course that best fits your preparation needs and timeline.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {courses.map((course) => (
              <Card 
                key={course.id} 
                className={`relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
                  course.popular ? 'ring-2 ring-gold-500' : ''
                }`}
              >
                {course.popular && (
                  <div className="absolute top-0 right-0">
                    <Badge className="bg-gold-500 text-navy-900 font-semibold rounded-none rounded-bl-lg px-4 py-1">
                      <Star className="w-3 h-3 mr-1 fill-current" /> Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="bg-navy-900 text-white p-8">
                  <CardTitle className="text-2xl font-bold mb-2">{course.name}</CardTitle>
                  <p className="text-gray-400 text-sm">{course.duration} Program</p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gold-400">₹{course.price.toLocaleString()}</span>
                    <span className="text-gray-400 text-sm ml-2">/ full course</span>
                  </div>
                </CardHeader>
                
                <CardContent className="p-8 bg-white">
                  <ul className="space-y-4 mb-8">
                    {course.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-olive-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={handleBookConsultation}
                    className={`w-full py-6 font-semibold transition-all duration-300 ${
                      course.popular 
                        ? 'bg-gold-500 hover:bg-gold-400 text-navy-900' 
                        : 'bg-navy-900 hover:bg-navy-800 text-white'
                    }`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Enroll Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-olive-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Not Sure Which Course to Choose?</h2>
          <p className="text-olive-200 mb-8 max-w-2xl mx-auto">
            Contact us for a free consultation with our experts who will guide you to the right program.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold text-lg px-10 py-6 transition-all duration-300 hover:shadow-xl">
              Get Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default CoursesPage;
